import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { useColorScheme } from "@mui/material/styles";
import { RiMenuFoldLine, RiMenuUnfoldLine, RiMoonLine, RiSearchLine, RiSunLine } from "@remixicon/react";
import { focusRing, tokens } from "../../utils/figmaTokens";
import { dateLines, shortcutLabel } from "../../utils/shellPresentation";
import Kbd from "../kit/Kbd";

const oneLine = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const iconButtonSx = { width: 28, height: 28, p: "6px", borderRadius: "7px", color: tokens.icon.default, "&:hover": { bgcolor: tokens.hover }, ...focusRing };

// Admin · Breadcrumb (10651:4816). On a phone only the last crumb shows.
const Breadcrumb = ({ crumbs }) => (
  <Box component="nav" aria-label="Breadcrumb" sx={{ minWidth: 0 }}>
    <Box component="ol" sx={{ display: "flex", alignItems: "center", gap: "4px", m: 0, p: 0, listStyle: "none", minWidth: 0 }}>
      {crumbs.map((crumb, i) => {
        const last = i === crumbs.length - 1;
        const { Icon } = crumb;
        const chipSx = {
          display: "flex",
          alignItems: "center",
          gap: "6px",
          px: "6px",
          py: "4px",
          borderRadius: "6px",
          minWidth: 0,
          typography: last ? "bodyMediumStrong" : "bodyMedium",
          color: last ? tokens.text.primary : tokens.text.secondary,
          textDecoration: "none",
        };
        const body = (
          <>
            {Icon && <Icon size={18} color={last ? tokens.icon.brand : tokens.icon.secondary} />}
            <Box component="span" sx={oneLine}>{crumb.label}</Box>
          </>
        );
        return (
          <React.Fragment key={`${i}-${crumb.label}`}>
            {i > 0 && (
              <Box component="li" aria-hidden sx={{ display: { xs: "none", sm: "block" }, typography: "bodyMedium", color: tokens.text.tertiary }}>
                /
              </Box>
            )}
            <Box component="li" sx={{ display: { xs: last ? "flex" : "none", sm: "flex" }, minWidth: 0 }}>
              {crumb.to && !last ? (
                <Box component={RouterLink} to={crumb.to} sx={{ ...chipSx, "&:hover": { bgcolor: tokens.hover } }}>
                  {body}
                </Box>
              ) : (
                <Box component="span" aria-current={last ? "page" : undefined} sx={chipSx}>
                  {body}
                </Box>
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  </Box>
);

// Admin · Top Bar (047 D11; 10660:6615): the collapse button, the breadcrumb, the search box,
// the dates, and the moon that switches Light and Dark (D107, P126). No user chip or bell (D11).
// On a phone the search box is an icon, and the dates and the line before the moon hide.
const TopBar = ({ crumbs, collapsed, onToggle, onSearch }) => {
  const { mode, setMode } = useColorScheme();
  const dark = mode === "dark";
  // ponytail: the dates are read on each render (every navigation), so a tab left open past
  // midnight shows yesterday until the next one; add a timer if staff notice.
  const { gregorian, hijri } = dateLines(new Date());
  const platform = navigator.userAgentData?.platform || navigator.platform || "";

  return (
    <Box
      component="header"
      sx={{
        height: 72,
        display: "flex",
        alignItems: "center",
        gap: "16px",
        pl: { xs: "12px", sm: "20px" },
        pr: { xs: "12px", sm: "24px" },
        bgcolor: tokens.surface.default,
        minWidth: 0,
      }}
    >
      <IconButton aria-label={collapsed ? "Open the menu" : "Collapse the menu"} onClick={onToggle} sx={iconButtonSx}>
        {collapsed ? <RiMenuUnfoldLine size={16} /> : <RiMenuFoldLine size={16} />}
      </IconButton>
      <Breadcrumb crumbs={crumbs} />
      <Box sx={{ flex: 1 }} />
      <ButtonBase
        onClick={onSearch}
        sx={{
          display: { xs: "none", sm: "flex" },
          flex: "0 1 340px",
          minWidth: 0,
          height: 40,
          gap: "10px",
          pl: "12px",
          pr: "8px",
          justifyContent: "flex-start",
          borderRadius: "10px",
          bgcolor: tokens.surface.muted,
          color: tokens.text.tertiary,
          typography: "bodySmall",
          ...focusRing,
        }}
      >
        <RiSearchLine size={16} />
        <Box component="span" sx={{ flex: 1, minWidth: 0, textAlign: "left", ...oneLine }}>
          Search everything…
        </Box>
        <Kbd>{shortcutLabel(platform)}</Kbd>
      </ButtonBase>
      <IconButton aria-label="Search" onClick={onSearch} sx={{ ...iconButtonSx, display: { xs: "inline-flex", sm: "none" } }}>
        <RiSearchLine size={16} />
      </IconButton>
      <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
        <Typography variant="bodySmallStrong" sx={{ color: tokens.text.primary, whiteSpace: "nowrap" }}>
          {gregorian}
        </Typography>
        <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary, whiteSpace: "nowrap" }}>
          {hijri}
        </Typography>
      </Box>
      <Box aria-hidden sx={{ display: { xs: "none", sm: "block" }, width: "1px", height: 28, flexShrink: 0, bgcolor: tokens.divider }} />
      <IconButton
        aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
        onClick={() => setMode(dark ? "light" : "dark")}
        sx={iconButtonSx}
      >
        {dark ? <RiSunLine size={16} /> : <RiMoonLine size={16} />}
      </IconButton>
    </Box>
  );
};

export default TopBar;
